'use client'

import React, { useRef, useState } from 'react'
import Image from 'next/image'
import { X, ChevronLeft, ChevronRight, Grid3X3 } from 'lucide-react'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import { Zoom, Navigation } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/zoom'
import './swiper-reset.css'

interface ImageModalProps {
  images: string[]
  currentIndex: number
  onClose: () => void
  onImageChange?: (index: number) => void
  productImagesCount?: number
  showCurrentStatusLabels?: boolean
}

const ImageModal: React.FC<ImageModalProps> = ({
  images,
  currentIndex,
  onClose,
  onImageChange,
  productImagesCount = 0,
  showCurrentStatusLabels = false,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(currentIndex)
  const [showThumbnails, setShowThumbnails] = useState(false)
  const swiperRef = useRef<SwiperRef['swiper'] | null>(null)

  if (images.length === 0) return null

  const isCurrentImage = (index: number) => index >= productImagesCount

  const handleSlideChange = (swiper: any) => {
    const newIndex = swiper.activeIndex
    setSelectedIndex(newIndex)
    onImageChange?.(newIndex)
  }

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index)
    onImageChange?.(index)
    swiperRef.current?.slideTo(index)
    if (window.innerWidth < 768) {
      setShowThumbnails(false)
    }
  }

  const handlePrevious = () => {
    swiperRef.current?.slidePrev()
  }

  const handleNext = () => {
    swiperRef.current?.slideNext()
  }

  return (
    <div className="fixed inset-0 bg-white flex z-50">
      {/* メイン画像エリア */}
      <div className="flex-1 flex items-center justify-center relative p-4">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 p-2 transition-colors z-10"
          aria-label="閉じる"
          title="閉じる"
        >
          <X className="w-8 h-8 md:w-9 md:h-9 text-gray-800 hover:text-gray-500" />
        </button>

        {/* モバイル用サムネイル表示ボタン */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setShowThumbnails(!showThumbnails)
          }}
          className="md:hidden absolute top-4 left-4 p-2 transition-colors z-10"
          aria-label="サムネイル表示"
          title="サムネイル表示"
        >
          <Grid3X3 className="w-8 h-8 text-gray-800 hover:text-gray-500" />
        </button>

        {/* ナビゲーションボタン */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handlePrevious()
          }}
          className="absolute left-4 md:left-20 top-1/2 -translate-y-1/2 p-3 transition-all z-10"
          aria-label="前の画像"
          title="前の画像"
        >
          <ChevronLeft className="w-10 h-10 md:w-12 md:h-12 text-gray-800 hover:text-gray-500" />
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleNext()
          }}
          className="absolute right-4 md:right-20 top-1/2 -translate-y-1/2 p-3 transition-all z-10"
          aria-label="次の画像"
          title="次の画像"
        >
          <ChevronRight className="w-10 h-10 md:w-12 md:h-12 text-gray-800 hover:text-gray-500" />
        </button>

        {/* Swiperメイン画像 */}
        <div className="image-modal relative w-full max-w-4xl h-[70vh] md:h-[80vh] flex items-center justify-center">
          <Swiper
            slidesPerView={1}
            initialSlide={currentIndex}
            navigation={{
              prevEl: '.image-modal-prev',
              nextEl: '.image-modal-next',
            }}
            zoom={true}
            modules={[Navigation, Zoom]}
            onActiveIndexChange={handleSlideChange}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            className="w-full h-full cursor-zoom-in"
          >
            {images.map((image, index) => (
              <SwiperSlide
                key={index}
                className="flex items-center justify-center relative"
              >
                <div className="swiper-zoom-container">
                  <Image
                    src={image}
                    alt={`商品画像 ${index + 1}`}
                    className="max-w-full max-h-full object-contain"
                    width={1200}
                    height={1200}
                    quality={100}
                    priority={index === currentIndex}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {/* 現状の写真ラベル */}
                {showCurrentStatusLabels && isCurrentImage(index) && (
                  <span className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-2 text-sm font-medium">
                    現状の写真
                  </span>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* 画像インジケーター（モバイル用） */}
        <div className="md:hidden absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
          {selectedIndex + 1} / {images.length}
        </div>
      </div>

      {/* サムネイルサイドバー（PC用は常時表示、モバイル用は切り替え） */}
      <div
        className={`
        bg-white shadow-xl transition-transform duration-300 ease-in-out
        md:w-96 md:translate-x-0 md:relative md:block
        ${showThumbnails ? 'translate-x-0' : 'translate-x-full'}
        w-full absolute right-0 top-0 h-full z-20
      `}
      >
        {/* モバイル用閉じるボタン */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setShowThumbnails(false)
          }}
          className="md:hidden absolute top-2 right-4 p-2 transition-colors z-10"
          aria-label="サムネイル閉じる"
          title="サムネイル閉じる"
        >
          <X className="w-8 h-8 text-gray-800 hover:text-gray-500" />
        </button>

        <div className="p-5 border-b border-gray-100">
          <div className="flex justify-between text-base font-semibold text-gray-700">
            <span>商品写真</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 max-h-[calc(100vh-70px)]">
          {showCurrentStatusLabels ? (
            <>
              {/* 新品の時の写真セクション */}
              {productImagesCount > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold mb-3 text-gray-700">
                    新品の時の写真
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {images.slice(0, productImagesCount).map((image, index) => (
                      <button
                        type="button"
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleThumbnailClick(index)
                        }}
                        title={`画像 ${index + 1} を表示`}
                        aria-label={`画像 ${index + 1} を表示`}
                        className={`relative overflow-hidden transition-all ${
                          selectedIndex === index
                            ? 'ring-2 ring-black ring-offset-2'
                            : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1'
                        }`}
                      >
                        <Image
                          src={image}
                          alt={`サムネイル ${index + 1}`}
                          className="w-full h-full object-cover"
                          width={160}
                          height={160}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 現状の写真セクション */}
              {images.length > productImagesCount && (
                <div>
                  <h3 className="text-sm font-bold mb-3 text-gray-700">
                    現状の写真
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {images.slice(productImagesCount).map((image, index) => {
                      const actualIndex = index + productImagesCount
                      return (
                        <button
                          type="button"
                          key={actualIndex}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleThumbnailClick(actualIndex)
                          }}
                          title={`現状の写真 ${index + 1} を表示`}
                          aria-label={`現状の写真 ${index + 1} を表示`}
                          className={`relative overflow-hidden transition-all ${
                            selectedIndex === actualIndex
                              ? 'ring-2 ring-black ring-offset-2'
                              : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1'
                          }`}
                        >
                          <Image
                            src={image}
                            alt={`現状のサムネイル ${index + 1}`}
                            className="w-full h-full object-cover"
                            width={160}
                            height={160}
                          />
                          {/* 現状の写真ラベル */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 text-center">
                            現状
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            // 通常のサムネイル表示（フラグがfalseの場合）
            <div className="grid grid-cols-2 gap-4">
              {images.map((image, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleThumbnailClick(index)
                  }}
                  title={`画像 ${index + 1} を表示`}
                  aria-label={`画像 ${index + 1} を表示`}
                  className={`relative overflow-hidden transition-all ${
                    selectedIndex === index
                      ? 'ring-2 ring-black ring-offset-2'
                      : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1'
                  }`}
                >
                  <Image
                    src={image}
                    alt={`サムネイル ${index + 1}`}
                    className="w-full h-full object-cover"
                    width={160}
                    height={160}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* モバイル用オーバーレイ（サムネイル表示時） */}
      {showThumbnails && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
          onClick={(e) => {
            e.stopPropagation()
            setShowThumbnails(false)
          }}
        />
      )}
    </div>
  )
}

export default ImageModal
